/**
 * Hook for fetching token prices in USD
 * Fetches from the backend /api/prices endpoint (which proxies Wombat/CoinGecko).
 * Prices are NOT visible in the browser network tab as external calls.
 */

import { useQuery } from '@tanstack/react-query'
import { Token } from '@contracts/tokens'

// Last-resort static fallback prices (used if /api/prices is unreachable)
const FALLBACK_PRICES: Record<string, number> = {
  ETH:    3350,
  WETH:   3350,
  USDC:   1,
  wBTC:   93500,
  cbBTC:  93500,
  wstETH: 3700,
  BTC:    93500,
  BCH:    400,
  USDT:   1,
}

interface PricesResponse {
  prices: Record<string, number>
  source: 'wombat' | 'coingecko' | 'fallback'
  timestamp: number
}

/**
 * Fetch all token prices from the backend proxy.
 * One batched call → no CoinGecko/Wombat visible in browser network tab.
 */
async function fetchAllPrices(): Promise<Record<string, number>> {
  try {
    const response = await fetch('/api/prices', {
      signal: AbortSignal.timeout(8000),
    })

    if (!response.ok) {
      console.warn(`⚠️ [Price] /api/prices returned ${response.status}, using fallback`)
      return { ...FALLBACK_PRICES }
    }

    const data: PricesResponse = await response.json()
    console.log(`✅ [Price] Prices loaded from ${data.source}`, data.prices)

    // Merge with fallback to ensure all tokens have a price
    return { ...FALLBACK_PRICES, ...data.prices }
  } catch (error: any) {
    const errorType =
      error.name === 'AbortError' ? 'timeout' :
      error.message?.includes('Failed to fetch') ? 'network' : 'unknown'

    console.warn(`⚠️ [Price] ${errorType} error fetching /api/prices, using static fallback`)
    return { ...FALLBACK_PRICES }
  }
}

/**
 * Hook to get all token prices in one batched query.
 * Refetches every 60 seconds (server caches for 30s on its end).
 */
function useAllPrices() {
  return useQuery({
    queryKey: ['allTokenPrices'],
    queryFn: fetchAllPrices,
    staleTime: 30 * 1000,       // consider stale after 30s
    refetchInterval: 60 * 1000, // refetch every 60s
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * Hook to get a single token price in USD.
 * Same API as before — just driven by the batched backend call.
 */
export function useTokenPrice(token?: Token) {
  const { data: prices, isLoading, error } = useAllPrices()

  const price = token && prices ? (prices[token.symbol] ?? FALLBACK_PRICES[token.symbol] ?? 0) : 0

  return {
    data: price,
    isLoading,
    error,
  }
}

/**
 * Hook to get multiple token prices.
 * Returns a Record<symbol, price> — same shape as before.
 */
export function useTokenPrices(tokens: Token[]) {
  const { data: allPrices, isLoading, error } = useAllPrices()

  const prices: Record<string, number> | undefined = allPrices
    ? Object.fromEntries(
        tokens.map(t => [t.symbol, allPrices[t.symbol] ?? FALLBACK_PRICES[t.symbol] ?? 0])
      )
    : undefined

  return {
    data: prices,
    isLoading,
    error,
  }
}

/**
 * Calculate fiat value — unchanged API
 */
export function calculateFiatValue(amount: string, price: number): string {
  if (!amount || isNaN(Number(amount)) || !price) {
    return '0.00'
  }
  const value = Number(amount) * price
  return value.toFixed(2)
}
